import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../AuthContext';

const FooterPagination = ({
  currentPage,
  setCurrentPage,
  getFilteredMessages,
  isDarkMode,
  setIsDarkMode,
  getTotalPages,
  recordsPerPage,
  setRecordsPerPage,
  isMobile,
  edgeServerEndpoint,
  reverseSort
}) => {
  const { user } = useAuth();
  const totalMessages = getFilteredMessages().length;
  const totalPages = getTotalPages(totalMessages);

  const startRecord = (currentPage - 1) * recordsPerPage + 1;
  const endRecord = Math.min(currentPage * recordsPerPage, totalMessages);

  // Save pagination preferences to backend
  const savePaginationPreferences = async (newRecordsPerPage, newCurrentPage) => {
    if (!user?.username) return;
    
    try {
      await axios.post(`${edgeServerEndpoint}/pagination-preferences/${user.username}`, {
        recordsPerPage: newRecordsPerPage,
        currentPage: newCurrentPage,
        reverseSort: reverseSort
      });
    } catch (error) {
      console.error('Failed to save pagination preferences:', error);
    }
  };

  // Handle records per page change
  const handleRecordsPerPageChange = (newValue) => {
    const newRecordsPerPage = Number(newValue);
    setRecordsPerPage(newRecordsPerPage);
    
    // Reset to first page when changing records per page
    const newCurrentPage = 1;
    setCurrentPage(newCurrentPage);
    
    // Save preferences
    savePaginationPreferences(newRecordsPerPage, newCurrentPage);
  };

  // Handle page navigation
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    savePaginationPreferences(recordsPerPage, newPage);
  };

  const commonStyles = {
    background: isDarkMode ? 'bg-gray-900' : 'bg-white',
    borderColor: isDarkMode ? 'border-[#ff8b2d]/30' : 'border-gray-300',
    textColor: isDarkMode ? 'text-white' : 'text-gray-800',
    secondaryTextColor: isDarkMode ? 'text-[#ff8b2d]' : 'text-orange-600',
    buttonBackground: isDarkMode ? 'bg-[#ff8b2d]/10' : 'bg-gray-200',
    buttonHoverBackground: isDarkMode ? 'hover:bg-[#ff8b2d]/20' : 'hover:bg-gray-300',
    disabledOpacity: isDarkMode ? 'disabled:opacity-30' : 'disabled:opacity-50',
    borderBackground: isDarkMode ? 'bg-[#ff8b2d]/5' : 'bg-gray-100',
  };

  return (
    <div
      className={`sticky bottom-0 ${commonStyles.background} border-t ${commonStyles.borderColor} ${commonStyles.textColor} z-10`}
    >
      <div className="max-w-7xl mx-auto px-2 md:px-4">
        <div className={`flex items-center ${isMobile ? 'justify-between h-12 py-1.5 gap-1' : 'justify-between h-16 space-x-4'}`}>
          {/* OLD button - left side on mobile */}
          <button
            onClick={() => {
              // When reverseSort=true: oldest at bottom (last page), so OLD goes to last page
              // When reverseSort=false: oldest at top (page 1), so OLD goes to page 1
              const newPage = reverseSort ? totalPages : 1;
              handlePageChange(newPage);
            }}
            disabled={reverseSort ? currentPage === totalPages : currentPage === 1}
            className={`flex items-center ${isMobile ? 'px-2 py-1 text-xs' : 'px-4 py-2'} rounded-lg ${commonStyles.buttonBackground} ${commonStyles.buttonHoverBackground} ${commonStyles.borderColor} ${commonStyles.disabledOpacity} transition-all duration-300 group flex-shrink-0`}
          >
            <span className={`${commonStyles.secondaryTextColor} font-medium ${isMobile ? 'text-xs' : 'text-base'}`}>OLD</span>
            <ChevronRight
              size={isMobile ? 12 : 16}
              className={`${isMobile ? 'ml-0.5' : 'ml-2'} ${commonStyles.secondaryTextColor} group-hover:transform group-hover:translate-x-0.5 transition-transform`}
            />
          </button>

          {/* Records per page dropdown - center on mobile */}
          <div className={`flex items-center ${isMobile ? 'flex-shrink-0' : 'space-x-2'}`}>
            {!isMobile && <label className="text-sm font-medium">Records:</label>}
            <select
              value={recordsPerPage}
              onChange={(e) => handleRecordsPerPageChange(e.target.value)}
              className={`${isMobile ? 'px-1.5 py-0.5 text-xs' : 'px-2 py-1 text-sm'} rounded border ${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'} focus:ring focus:ring-orange-300 focus:outline-none`}
            >
              {[10, 20, 50, 100].map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          {/* Showing count - center on mobile */}
          <div className={`text-center ${isMobile ? 'px-1.5 py-0.5 text-xs flex-1 min-w-0' : 'px-3 py-1 flex-1'} rounded-lg ${commonStyles.borderBackground} ${commonStyles.borderColor}`}>
            <span className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium`}>
              <span className={commonStyles.secondaryTextColor}>
                {startRecord}-{endRecord}
              </span>
              {!isMobile && ' of '}
              {isMobile && ' / '}
              <span className={commonStyles.secondaryTextColor}>
                {totalMessages}
              </span>
            </span>
          </div>

          {/* NEW button - right side on mobile */}
          <button
            onClick={() => {
              // When reverseSort=true: newest at top (page 1), so NEW goes to page 1
              // When reverseSort=false: newest at bottom (last page), so NEW goes to last page
              const newPage = reverseSort ? 1 : totalPages;
              handlePageChange(newPage);
            }}
            disabled={reverseSort ? currentPage === 1 : currentPage === totalPages}
            className={`flex items-center ${isMobile ? 'px-2 py-1 text-xs' : 'px-4 py-2'} rounded-lg ${commonStyles.buttonBackground} ${commonStyles.buttonHoverBackground} ${commonStyles.borderColor} ${commonStyles.disabledOpacity} transition-all duration-300 group flex-shrink-0`}
          >
            <ChevronLeft
              size={isMobile ? 12 : 16}
              className={`${isMobile ? 'mr-0.5' : 'mr-2'} ${commonStyles.secondaryTextColor} group-hover:transform group-hover:-translate-x-0.5 transition-transform`}
            />
            <span className={`${commonStyles.secondaryTextColor} font-medium ${isMobile ? 'text-xs' : 'text-base'}`}>NEW</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default FooterPagination;